import React, { useState } from 'react';
import { View, Text, Pressable, Modal, type PressableProps } from 'react-native';
import { KeyboardController } from 'react-native-keyboard-controller';
import { cn } from '@/utils/cn';

export interface SelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
  placeholder?: string;
  className?: string;
  /** Posição do modal: 'center' (padrão) ou 'top'. Padrão: center. */
  dropdownPlacement?: 'top' | 'center';
  testID?: string;
}

export function Select({
  value,
  onValueChange,
  children,
  placeholder = 'Select...',
  className,
  dropdownPlacement = 'center',
  testID,
}: SelectProps) {
  const [open, setOpen] = useState(false);

  return (
    <View className={cn('relative', className)}>
      <Pressable
        onPress={() => void KeyboardController.dismiss().then(() => setOpen(true))}
        testID={testID}
        className={cn(
          'min-h-[48px] flex-row items-center justify-between rounded-xl border border-neutral-200 bg-card px-4 py-3',
          'focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
        style={{ borderCurve: 'continuous' }}
      >
        <Text className={cn('flex-1 text-base', value ? 'text-foreground' : 'text-neutral-400')}>
          {value || placeholder}
        </Text>
        <Text className="text-neutral-400">▼</Text>
      </Pressable>
      {open && (
        <Modal
          visible={open}
          transparent
          animationType="fade"
          onRequestClose={() => setOpen(false)}
        >
          <Pressable
            className="flex-1 items-center justify-center bg-black/50"
            onPress={() => setOpen(false)}
          >
            <View className="w-[90%] max-w-sm rounded-xl border border-border bg-card p-2">
              {React.Children.map(children, (child) => {
                if (React.isValidElement(child)) {
                  return React.cloneElement(child as React.ReactElement<any>, {
                    onSelect: (val: string) => {
                      onValueChange?.(val);
                      setOpen(false);
                    },
                    selectedValue: value,
                  });
                }
                return child;
              })}
            </View>
          </Pressable>
        </Modal>
      )}
    </View>
  );
}

export interface SelectItemProps extends PressableProps {
  value: string;
  children: React.ReactNode;
  onSelect?: (value: string) => void;
  selectedValue?: string;
  className?: string;
}

export function SelectItem({
  value,
  children,
  onSelect,
  selectedValue,
  className,
  ...props
}: SelectItemProps) {
  const isSelected = selectedValue === value;

  return (
    <Pressable
      onPress={() => onSelect?.(value)}
      className={cn(
        'relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
        'focus:bg-primary focus:text-primary-foreground',
        isSelected && 'bg-primary text-primary-foreground',
        className,
      )}
      {...props}
    >
      {typeof children === 'string' ? (
        <Text
          className={cn('text-foreground', isSelected && 'font-medium text-primary-foreground')}
        >
          {children}
        </Text>
      ) : (
        children
      )}
    </Pressable>
  );
}
